'use client';
import { useId, useState } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SearchPartnerAddress } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-address.component';
import { SearchPartnerProfileAddress } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-profile-address.component';
import { SearchPartnerProfile } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-profile.component';
import { SearchPromotionalPricePill } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-promotional-pill.component';
import { SearchTag } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-tag.component';
import { SearchCardScheduleToggle } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_primitives/search-card-schedule-toggle.component';
import { useExpandedSlotsGrid } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-expanded-slots-grid.hook';
import { InstallmentTerms } from '@/components/ui/installment-terms.component';
import { SearchCardClinicTabs } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/variants/clinic/_components/tabs/search-card-clinic-tabs.component';
import { ScheduleTabOrigin } from '@/types/enums/analytics/search/search-analytics.enum';
import { ScheduleTabAnalytics } from '@/app/(main)/(pages)/busca/[slug]/_utils/schedule-tab-analytics.util';
import { SearchModalityAnalyticsMap } from '@/app/(main)/(pages)/busca/[slug]/_types/search-sort.type';
import { SearchCardComponentProps } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/search-card.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { isPreciseLocation } from '@/utils/formatters/location/is-precise-location.util';
import { userLocationStore } from '@/store/user-location.store';
import { SearchType } from '@/types/most-search-type';
import {
  getProcedurePromotionIds,
  hasProceduresPromotion,
  sumProcedureOriginalPrices,
  sumProcedurePrices,
} from '@/utils/procedures/procedures.util';
import { usePromotionSalesStatus } from '@/hooks/use-promotion-sales-status.hook';
import { EntitySpecialtiesAndRqe } from '@/app/(main)/(pages)/_components/entities/entity-specialties-and-rqe.component';

export const SearchCardClinic = ({
  schedule,
  searchType,
  hasTopHr,
  fluid,
}: SearchCardComponentProps) => {
  const { partner, professional, procedures, distance } = schedule;
  const tabAnalytics: ScheduleTabAnalytics = {
    origin: ScheduleTabOrigin.SEARCH_RESULTS,
    modalidade: SearchModalityAnalyticsMap[searchType],
  };
  const hasPreciseLocation = useStore(userLocationStore, (state) => isPreciseLocation(state?.userLocation));
  const { isCompact: isCollapsible } = useExpandedSlotsGrid();
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const scheduleId = useId();

  const hasPromotion = hasProceduresPromotion(procedures);
  const { isUrgent: isPromotionUrgent } = usePromotionSalesStatus(
    getProcedurePromotionIds(procedures),
    hasPromotion,
  );
  const showsPromotionalPill = hasPromotion && !isPromotionUrgent;
  const totalPrice = sumProcedurePrices(procedures);
  const hasProfessional = Boolean(professional?.id);

  const card = (
    <div className="bg-white border border-gray-300 rounded-2xl py-3 sm:py-6 xl:px-6 w-full" data-testid={DataTestIdEnum.SEARCH_CARD_CONTAINER}>
      <div className="xl:grid xl:grid-cols-2 xl:items-start xl:gap-6">
        <div className="px-3 sm:px-6 xl:pl-0 xl:pr-6">
          {showsPromotionalPill && (
            <SearchPromotionalPricePill
              originalPrice={sumProcedureOriginalPrices(procedures)}
              promotionalPrice={sumProcedurePrices(procedures)}
            />
          )}
          <SearchTag
            professional={professional}
            isPromotionUrgent={isPromotionUrgent}
            isHiddenByPromotionalPill={showsPromotionalPill}
          />
          <SearchPartnerProfile
            professional={professional}
            partner={partner}
            searchType={searchType}
            distance={distance || 0}
          />

          {hasProfessional && (
            <div className="mt-2">
              <EntitySpecialtiesAndRqe professional={professional} />
            </div>
          )}

          <div className="hidden xl:block">
            <SearchPartnerProfileAddress
              partner={partner}
              telehealth={searchType === SearchType.ONLINE_CONSULTATION}
              distance={distance || 0}
            />
            <div className="mt-4">
              <InstallmentTerms
                totalPrice={totalPrice}
              />
            </div>
          </div>

          <div className="xl:hidden">
            <SearchPartnerAddress
              partner={partner}
              telehealth={searchType === SearchType.ONLINE_CONSULTATION}
              isPreciseLocation={hasPreciseLocation}
              distance={distance || 0}
            />
          </div>
        </div>
        {isCollapsible
          ? (
              <div className="mt-4 flex flex-col gap-3">
                <div className="px-3 sm:px-6">
                  <SearchCardScheduleToggle
                    totalPrice={totalPrice}
                    isOpen={isScheduleOpen}
                    controlsId={scheduleId}
                    onToggle={() => setIsScheduleOpen((isOpen) => !isOpen)}
                  />
                </div>
                {isScheduleOpen && (
                  <div
                    id={scheduleId}
                    className="px-3 sm:px-6 pt-6 border-t border-gray-300"
                    data-testid={DataTestIdEnum.SEARCH_CARD_SCHEDULE_COLLAPSIBLE}
                  >
                    <SearchCardClinicTabs schedule={schedule} tabAnalytics={tabAnalytics} />
                  </div>
                )}
              </div>
            )
          : (
              <>
                <div className="mt-4 py-4 px-3 sm:px-6 border-y border-gray-300 xl:mt-0 xl:py-4 xl:px-4 xl:border xl:rounded-lg xl:bg-gray-50 xl:w-full">
                  <SearchCardClinicTabs schedule={schedule} tabAnalytics={tabAnalytics} />
                </div>

                <div className="mt-4 px-3 sm:px-6 xl:hidden">
                  <InstallmentTerms
                    totalPrice={totalPrice}
                  />
                </div>
              </>
            )}
      </div>
    </div>
  );

  return (
    <>
      {hasTopHr && <hr className="border-gray-300 w-full md:hidden" />}
      {fluid ? card : <ContentContainer className="md:px-0 md:mx-auto md:max-w-[576px] lg:max-w-3xl md:bg-gray-50">{card}</ContentContainer>}
    </>
  );
};
